import { Loader } from '@googlemaps/js-api-loader';

const GOOGLE_MAPS_API_KEY = 'AIzaSyAypd6pXePpssue2T-cQYzH7lSj-aLEE1g';
const GOOGLE_MAPS_API_ENDPOINT = 'https://maps.googleapis.com/maps/api/staticmap';
const DARK_MODE_STYLE_PARAMS =
  '&style=element%3Ageometry%7Ccolor%3A0x242f3e&style=element%3Alabels.text.stroke%7Ccolor%3A0x242f3e&style=element%3Alabels.text.fill%7Ccolor%3A0x746855&style=feature%3Aadministrative.locality%7Celement%3Alabels.text.fill%7Ccolor%3A0xd59563&style=feature%3Apoi%7Cvisibility%3Aoff&style=feature%3Apoi.park%7Celement%3Ageometry%7Ccolor%3A0x263c3f&style=feature%3Apoi.park%7Celement%3Alabels.text.fill%7Ccolor%3A0x6b9a76&style=feature%3Aroad%7Celement%3Ageometry%7Ccolor%3A0x38414e&style=feature%3Aroad%7Celement%3Ageometry.stroke%7Ccolor%3A0x212a37&style=feature%3Aroad%7Celement%3Alabels.text.fill%7Ccolor%3A0x9ca5b3&style=feature%3Aroad.highway%7Celement%3Ageometry%7Ccolor%3A0x746855&style=feature%3Aroad.highway%7Celement%3Ageometry.stroke%7Ccolor%3A0x1f2835&style=feature%3Aroad.highway%7Celement%3Alabels.text.fill%7Ccolor%3A0xf3d19c&style=feature%3Atransit%7Celement%3Ageometry%7Ccolor%3A0x2f3948&style=feature%3Atransit.station%7Celement%3Alabels.text.fill%7Ccolor%3A0xd59563&style=feature%3Awater%7Celement%3Ageometry%7Ccolor%3A0x17263c&style=feature%3Awater%7Celement%3Alabels.text.fill%7Ccolor%3A0x515c6d&style=feature%3Awater%7Celement%3Alabels.text.stroke%7Ccolor%3A0x17263';

export type GooglePlaceType = 'street_address' | 'premise' | 'subpremise' | 'establishment' | 'airport';

export type PlaceResult = {
  place_id: string;
  description: string;
};

type GoogleMapConfig = {
  address: string;
  width: number;
  height: number;
  scale?: number;
  zoom?: number;
  mapType?: 'roadmap' | 'satellite' | 'hybrid' | 'terrain';
  markerHexCode?: string;
};

type PlacesAutocompleteConfig = {
  input: string;
  types?: Array<GooglePlaceType>;
  regionCodes?: Array<string>;
  language?: string;
};

type PlaceDetailsConfig = {
  place_id: string;
  fields?: string[];
  language?: string;
};

export class GoogleMapsClient {
  private loader: Loader;
  private static instance: GoogleMapsClient;

  constructor() {
    this.loader = new Loader({
      apiKey: GOOGLE_MAPS_API_KEY,
      version: 'weekly',
      libraries: ['places'],
    });
  }

  public async getPlacesAutocomplete(config: PlacesAutocompleteConfig): Promise<{ predictions: Array<PlaceResult> }> {
    const { AutocompleteSuggestion } = (await this.loader.importLibrary('places')) as google.maps.PlacesLibrary;

    try {
      const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: config.input,
        includedPrimaryTypes: config.types,
        includedRegionCodes: config.regionCodes,
      });

      // Transform to match the old API format
      const predictions = response.suggestions.map((suggestion) => ({
        place_id: suggestion.placePrediction?.placeId || '',
        description: suggestion.placePrediction?.text?.text || '',
      }));

      return { predictions };
    } catch (error) {
      throw new Error(`Places service failed: ${error}`);
    }
  }

  public async getPlaceDetails(config: PlaceDetailsConfig) {
    try {
      const { Place } = (await this.loader.importLibrary('places')) as google.maps.PlacesLibrary;

      // Create a Place instance with the place ID
      const place = new Place({
        id: config.place_id,
      });

      // Fetch the place details
      const result = await place.fetchFields({
        fields: config.fields || [],
      });

      return { result: result.place };
    } catch (error) {
      throw new Error(`Place details failed: ${error}`);
    }
  }

  /**
   * Generates a URL for a static Google Maps image
   */
  public getStaticMapUrl(config: GoogleMapConfig): string {
    const { address, width, height, scale = 2, zoom = 15, mapType = 'roadmap', markerHexCode = '0xFFD670' } = config;
    const params = new URLSearchParams();
    params.set('size', `${width}x${height}`);
    params.set('scale', scale.toString());
    params.set('zoom', zoom.toString());
    params.set('maptype', mapType);
    params.set('markers', `size:mid|color:${markerHexCode}|${address}`);
    params.set('key', GOOGLE_MAPS_API_KEY);
    return `${GOOGLE_MAPS_API_ENDPOINT}?${params.toString()}${DARK_MODE_STYLE_PARAMS}`;
  }

  public static getInstance(): GoogleMapsClient {
    if (!GoogleMapsClient.instance) {
      GoogleMapsClient.instance = new GoogleMapsClient();
    }
    return GoogleMapsClient.instance;
  }
}

export default GoogleMapsClient.getInstance();
